import React, { useRef, useState } from 'react';
import { proService, vendorService } from '../../../services';
import { ClubPanel, PanelHead, PillButton, TagPill, useArmed, INK, MUTED, SUBTLE, INSET, CHIP, RED_TINT, RULE, inputStyle, textareaStyle, ringOn } from '../../club/clubUi';
import { money, monthDay } from './vendorUi';

const blank = () => ({ title: '', valueText: '', valueCents: null, terms: '', photoPath: null, photoUrl: null, quantity: null, usageMode: 'one_per_guest', status: 'draft', expiresAt: null });
const PHASE = { live: ['LIVE', '#22C55E', '#fff', null], ran_out: ['RAN OUT', '#EAB308', '#000', null], draft: ['DRAFT', '#fff', MUTED, '#D1D5DB'], ended: ['ENDED', '#E5E7EB', '#000', null] };
const TILES = ['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FAE1B4'];
const fieldLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };
const pillInput = { ...inputStyle, height: 50, fontSize: 15 };

/**
 * OFFERS (Pro dashboard handoff): the list of a place's offers — a tinted tile with the short
 * value, the title, a meta line, the phase pill and EDIT — and the inline editor (WHAT THEY GET,
 * VALUE, HOW MANY, SAY IT THEIR WAY, USAGE, THE SMALL PRINT, PHOTO) with CANCEL / SAVE DRAFT /
 * PUBLISH. Two independent limits: per guest and total. REMOVE is a two-step confirm.
 */
export default function OfferEditor({ offers, onChanged, mobile = false, say = null }) {
  const { armed, confirm, disarm } = useArmed(5000);
  const [editing, setEditing] = useState(null); // offer object or null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const save = async (status) => {
    if (!editing.title.trim()) { setError('Say what they get.'); return; }
    setBusy(true); setError('');
    const input = {
      title: editing.title, valueText: editing.valueText || null, valueCents: editing.valueCents || null, terms: editing.terms || null,
      photoPath: editing.photoPath || null, quantity: editing.quantity || null, usageMode: editing.usageMode, status,
      expiresAt: editing.expiresAt ? new Date(`${editing.expiresAt}T23:59:59`).toISOString() : null,
    };
    const r = editing.id ? await vendorService.updateOffer(editing.id, input) : await vendorService.createOffer(input);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the offer.'); return; }
    setEditing(null);
    say?.(status === 'published' ? 'OFFER IS LIVE' : 'DRAFT SAVED');
    onChanged?.();
  };
  const remove = async (o) => {
    if (!confirm(`remove-${o.id}`)) return;
    await vendorService.deleteOffer(o.id);
    setEditing(null);
    say?.('OFFER REMOVED');
    onChanged?.();
  };
  const onPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    const r = await proService.uploadAsset(file);
    setBusy(false);
    if (r.success) setEditing((e) => ({ ...e, photoPath: r.data.path, photoUrl: r.data.url }));
  };
  const phasePill = (o) => {
    const [t, bg, fg, ring] = PHASE[o.phase] || PHASE.draft;
    return <TagPill bg={bg} fg={fg} ring={ring} size={8} padding="4px 10px">{t}</TagPill>;
  };
  const twoCols = mobile ? 'minmax(0,1fr)' : 'repeat(2, minmax(0,1fr))';

  if (editing) {
    const e = editing;
    const set = (patch) => setEditing((x) => ({ ...x, ...patch }));
    const ok = !!e.title.trim();
    const rmArmed = armed === `remove-${e.id}`;
    return (
      <ClubPanel phone={mobile} style={{ gap: 16, boxShadow: `inset 0 0 0 2px ${INK}` }} data-testid="offer-editor">
        <PanelHead title={e.id ? 'EDIT OFFER' : 'NEW OFFER'}>
          {e.id && <PillButton variant={rmArmed ? 'armed' : 'danger'} size={32} onClick={() => remove(e)} data-testid="remove-offer">{rmArmed ? 'REMOVE IT' : 'REMOVE'}</PillButton>}
        </PanelHead>
        {rmArmed && <span style={{ padding: '12px 16px', borderRadius: 16, background: RED_TINT, fontSize: 12, lineHeight: 1.55, color: '#7F1D1D' }}>Take "{e.title}" down? Guests holding a claim keep their record.</span>}
        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={fieldLabel}>WHAT THEY GET</span>
          <input id="of-title" value={e.title} onChange={(ev) => set({ title: ev.target.value })} placeholder="Free plate of wings with any two mains" maxLength={120} style={pillInput} />
        </label>
        <div className="grid" style={{ gridTemplateColumns: twoCols, gap: 12 }}>
          <label className="flex flex-col" style={{ gap: 8 }}>
            <span style={fieldLabel}>VALUE</span>
            <span className="flex items-center" style={{ height: 50, padding: '0 20px', borderRadius: 999, background: INSET }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>$</span>
              <input id="of-value" type="number" min="0" step="0.5" inputMode="decimal" value={e.valueCents != null ? e.valueCents / 100 : ''} onChange={(ev) => set({ valueCents: ev.target.value ? Math.round(Number(ev.target.value) * 100) : null })} placeholder="18.00" className="min-w-0 flex-1" style={{ border: 'none', background: 'none', boxShadow: 'none', padding: '0 6px', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, color: INK, outline: 'none' }} />
            </span>
          </label>
          <label className="flex flex-col" style={{ gap: 8 }}>
            <span style={fieldLabel}>HOW MANY</span>
            <input id="of-qty" type="number" min="1" inputMode="numeric" value={e.quantity ?? ''} onChange={(ev) => set({ quantity: ev.target.value ? Number(ev.target.value) : null })} placeholder="100" style={{ ...pillInput, fontWeight: 800 }} />
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: SUBTLE }}>STOPS WHEN THEY RUN OUT. BLANK MEANS NO CAP.</span>
          </label>
        </div>
        <div className="grid" style={{ gridTemplateColumns: twoCols, gap: 12 }}>
          <label className="flex flex-col" style={{ gap: 8 }}>
            <span style={fieldLabel}>SAY IT THEIR WAY</span>
            <input id="of-valuetext" value={e.valueText || ''} onChange={(ev) => set({ valueText: ev.target.value })} placeholder="$10 OFF · FREE APPETIZER — shown on the card instead of the dollar value" maxLength={80} style={{ ...pillInput, fontSize: 14 }} />
          </label>
          <label className="flex flex-col" style={{ gap: 8 }}>
            <span style={fieldLabel}>RUNS UNTIL</span>
            <input id="of-until" type="date" value={e.expiresAt || ''} onChange={(ev) => set({ expiresAt: ev.target.value || null })} style={{ ...pillInput, fontSize: 14 }} />
          </label>
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span style={fieldLabel}>USAGE</span>
          <div className="grid" role="radiogroup" aria-label="Usage" style={{ gridTemplateColumns: twoCols, gap: 8 }}>
            {[['one_per_guest', 'ONE PER GUEST', 'Each person can claim and use it once. The QR dies on the first scan and the offer disappears from their wallet as claimable.'], ['unlimited_per_guest', 'UNLIMITED PER GUEST', 'They can claim it again after each use. A fresh code every time — good for a weekly night, dangerous on anything expensive.']].map(([k, t, b]) => {
              const on = e.usageMode === k;
              return (
                <button key={k} type="button" role="radio" aria-checked={on} onClick={() => set({ usageMode: k })} className="flex flex-col text-left font-mono" style={{ gap: 6, padding: '16px 18px', border: '2px solid transparent', borderRadius: 20, background: on ? ringOn : INSET, color: on ? '#fff' : INK, cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>{t}</span>
                  <span style={{ fontSize: 11, lineHeight: 1.55, opacity: 0.75 }}>{b}</span>
                </button>
              );
            })}
          </div>
        </div>
        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={fieldLabel}>THE SMALL PRINT</span>
          <textarea id="of-terms" rows={3} value={e.terms || ''} onChange={(ev) => set({ terms: ev.target.value })} placeholder="Dine-in only. One per table. Not with other offers." maxLength={600} style={{ ...textareaStyle, padding: '14px 20px', fontSize: 13, lineHeight: 1.6 }} />
        </label>
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="flex justify-between" style={{ gap: 10 }}><span style={fieldLabel}>PHOTO</span><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: SUBTLE }}>OPTIONAL</span></span>
          <button type="button" onClick={() => fileRef.current?.click()} className="relative overflow-hidden font-mono hover:border-black" style={{ height: 140, border: '2px dashed #D1D5DB', borderRadius: 20, background: INSET, cursor: 'pointer', transition: 'border-color .2s' }}>
            {e.photoUrl ? <img src={e.photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <span style={{ fontSize: 11, color: MUTED }}>Offer photo — skip it and the card runs without one</span>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(ev) => { onPhoto(ev.target.files?.[0]); ev.target.value = ''; }} />
          {e.photoUrl && <PillButton variant="tertiary" size={32} onClick={() => set({ photoPath: null, photoUrl: null })} style={{ alignSelf: 'flex-start' }}>REMOVE PHOTO</PillButton>}
        </div>
        {error && <span style={{ padding: '12px 16px', borderRadius: 16, background: RED_TINT, fontSize: 12, color: '#991B1B' }} role="alert">{error}</span>}
        <span className="flex flex-wrap justify-end" style={{ gap: 8 }}>
          <PillButton variant="tertiary" size={46} onClick={() => { disarm(); setEditing(null); }} disabled={busy}>CANCEL</PillButton>
          <PillButton variant="secondary" size={46} onClick={() => save('draft')} disabled={busy} style={{ opacity: ok ? 1 : 0.35 }}>{busy ? 'SAVING…' : 'SAVE DRAFT'}</PillButton>
          <PillButton size={46} onClick={() => save('published')} disabled={busy} style={{ opacity: ok ? 1 : 0.35 }}>{busy ? 'SAVING…' : 'PUBLISH'}</PillButton>
        </span>
      </ClubPanel>
    );
  }

  return (
    <ClubPanel phone={mobile} style={{ gap: 6 }} data-testid="offers-panel">
      <PanelHead title="OFFERS">
        <PillButton size={36} onClick={() => setEditing(blank())} data-testid="new-offer">+ NEW OFFER</PillButton>
      </PanelHead>
      {(!offers || offers.length === 0) && (
        <span style={{ padding: '18px 20px', borderRadius: 18, background: INSET, fontSize: 12, lineHeight: 1.65, color: MUTED }}>Nothing posted yet. An offer is something a Portal guest claims here and carries in — a free plate, ten dollars off a bottle. Portal takes no money for it.</span>
      )}
      {(offers || []).map((o, i) => {
        const short = (o.valueText || (o.valueCents ? money(o.valueCents, { whole: true }) : '')).slice(0, 10) || '—';
        const meta = [o.usageMode === 'unlimited_per_guest' ? 'UNLIMITED' : 'ONE PER GUEST', o.valueCents ? money(o.valueCents) : null, o.expiresAt ? `UNTIL ${monthDay(o.expiresAt)}` : null, o.quantity ? `${o.claimedCount || 0}/${o.quantity} CLAIMED` : null].filter(Boolean).join(' · ');
        return (
          <div key={o.id} className="flex flex-wrap items-center" style={{ gap: '8px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid="offer-list-row">
            <span className="flex flex-shrink-0 items-center justify-center text-center" style={{ width: 52, height: 52, borderRadius: 14, background: TILES[i % TILES.length], padding: 4, fontSize: 10, fontWeight: 800, letterSpacing: '.06em', lineHeight: 1.1, boxSizing: 'border-box', textTransform: 'uppercase' }}>{short}</span>
            <span className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>{o.title}</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: MUTED }}>{meta}</span>
            </span>
            <span style={{ fontSize: 13, fontWeight: 800 }}>{o.redeemedCount} / {o.claimedCount}</span>
            {phasePill(o)}
            <PillButton variant="tertiary" size={32} onClick={() => setEditing({ ...o, expiresAt: o.expiresAt ? o.expiresAt.slice(0, 10) : null })} style={{ background: CHIP }} aria-label={`Edit ${o.title}`}>EDIT</PillButton>
          </div>
        );
      })}
    </ClubPanel>
  );
}
